import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Search, Printer, ChevronDown } from 'lucide-react';

const STATUS_OPTIONS = ['pending','printed','packed','shipped','delivered','cancelled'];
const STATUS_COLORS = { pending:'#6c757d', printed:'#3b82f6', packed:'#f59e0b', shipped:'#8b5cf6', delivered:'#22c55e', cancelled:'#ef4444' };
const DELIVERY_CO = { own:'🛵 Own', jnt:'📦 J&T', vet:'🚚 VET', other:'Other' };

const s = {
  page: { maxWidth: 1000 },
  h1: { fontSize: 22, fontWeight: 700, color: '#212529', marginBottom: 4 },
  sub: { fontSize: 13, color: '#6c757d', marginBottom: 24 },
  toolbar: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' },
  searchBox: { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: '8px 12px', flex: 1, maxWidth: 300 },
  searchInput: { border: 'none', outline: 'none', fontSize: 13, color: '#212529', background: 'transparent', width: '100%' },
  select: { background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: '8px 12px', color: '#212529', fontSize: 13 },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #dee2e6' },
  th: { padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', background: '#f8f9fa', borderBottom: '1px solid #dee2e6' },
  td: { padding: '12px 16px', borderBottom: '1px solid #f1f3f5', fontSize: 13, color: '#212529' },
  statusBadge: (s) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: (STATUS_COLORS[s] || '#6c757d') + '20', color: STATUS_COLORS[s] || '#6c757d' }),
  btnSm: (color) => ({ background: color, color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', marginRight: 4 }),
  modal: { position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 },
  modalBox: { background: '#fff', borderRadius: 16, padding: 28, width: 520, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
};

export default function Shipments() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [tracking, setTracking] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => { loadBills(); }, [statusFilter]);

  async function loadBills() {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const r = await api.get('/billing/bills', { params });
      setBills(r.data.bills || []);
    } catch { toast.error('Failed to load bills'); }
    finally { setLoading(false); }
  }

  async function openDetail(id) {
    try {
      const r = await api.get(`/billing/bills/${id}`);
      setDetail(r.data);
      setTracking(r.data.tracking_number || '');
      setCancelReason('');
    } catch { toast.error('Failed to load bill'); }
  }

  async function updateStatus(id, newStatus) {
    const confirmed = window.confirm(`Update bill status to "${newStatus}"?`);
    if (!confirmed) return;
    try {
      await api.put(`/billing/bills/${id}/status`, {
        status: newStatus,
        tracking_number: tracking,
        cancel_reason: cancelReason
      });
      toast.success(`Status → ${newStatus}`);
      setDetail(null);
      loadBills();
    } catch { toast.error('Failed to update'); }
  }

  async function handleSearch(e) {
    e.preventDefault();
    loadBills();
  }

  function printBill(id) {
    window.open(`/billprint?bill_id=${id}`, '_blank');
  }

  return (
    <div style={s.page}>
      <div style={s.h1}>📦 Shipments</div>
      <div style={s.sub}>Track all printed bills and delivery status</div>

      <div style={s.toolbar}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
          <div style={s.searchBox}>
            <Search size={14} color="#6c757d" />
            <input style={s.searchInput} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search bill#, customer, phone..." />
          </div>
          <button type="submit" style={{ background: '#7c6af5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}>Search</button>
        </form>
        <select style={s.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <table style={s.table}>
        <thead>
          <tr>
            {['Bill#', 'Customer', 'Delivery', 'Total', 'Status', 'Date', 'Actions'].map(h => (
              <th key={h} style={s.th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bills.map(b => (
            <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(b.id)}>
              <td style={{ ...s.td, fontFamily: 'monospace', color: '#7c6af5' }}>{b.bill_number}</td>
              <td style={s.td}>
                <div style={{ fontWeight: 600 }}>{b.customer_name}</div>
                <div style={{ fontSize: 11, color: '#495057' }}>{b.customer_phone}</div>
              </td>
              <td style={s.td}>
                <div>{DELIVERY_CO[b.delivery_company] || b.delivery_company}</div>
                <div style={{ fontSize: 11, color: '#495057' }}>{b.delivery_type?.toUpperCase()}</div>
              </td>
              <td style={{ ...s.td, fontWeight: 700, color: '#7c6af5' }}>${b.total.toFixed(2)}</td>
              <td style={s.td}><span style={s.statusBadge(b.status)}>{b.status}</span></td>
              <td style={{ ...s.td, color: '#495057', fontSize: 12 }}>{b.created_at}</td>
              <td style={s.td} onClick={e => e.stopPropagation()}>
                <button style={s.btnSm('#6c757d')} onClick={() => printBill(b.id)}>🖨 Print</button>
              </td>
            </tr>
          ))}
          {!loading && bills.length === 0 && (
            <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: '#6c757d' }}>No bills found</td></tr>
          )}
        </tbody>
      </table>

      {/* Detail Modal */}
      {detail && (
        <div style={s.modal} onClick={() => setDetail(null)}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 17 }}>{detail.bill_number}</div>
                <div style={{ fontSize: 12, color: '#495057' }}>{detail.customer_name} · {detail.customer_phone}</div>
              </div>
              <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6c757d' }}>✕</button>
            </div>

            {/* Status badge */}
            <div style={{ marginBottom: 16 }}>
              <span style={s.statusBadge(detail.status)}>{detail.status}</span>
            </div>

            {/* Orders & items */}
            {detail.orders?.map(order => (
              <div key={order.id} style={{ background: '#f8f9fa', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: '#7c6af5', fontFamily: 'monospace', marginBottom: 6 }}>{order.order_number}</div>
                {order.items.map((item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span>{item.name}{item.code ? ` (${item.code})` : ''} × {item.qty}</span>
                    <span style={{ fontWeight: 600 }}>${item.subtotal.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ))}

            {/* Totals */}
            <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 12, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6c757d', marginBottom: 4 }}><span>Subtotal</span><span>${detail.subtotal.toFixed(2)}</span></div>
              {detail.delivery_fee > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6c757d', marginBottom: 4 }}><span>Delivery ({DELIVERY_CO[detail.delivery_company]})</span><span>${detail.delivery_fee.toFixed(2)}</span></div>}
              {detail.discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#ef4444', marginBottom: 4 }}><span>Discount</span><span>-${detail.discount.toFixed(2)}</span></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, borderTop: '1px solid #dee2e6', paddingTop: 8, marginTop: 4 }}><span>Total</span><span style={{ color: '#7c6af5' }}>${detail.total.toFixed(2)}</span></div>
            </div>

            {/* Tracking number for shipped */}
            {['printed','packed'].includes(detail.status) && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Tracking Number (optional)</label>
                <input value={tracking} onChange={e => setTracking(e.target.value)} placeholder="Enter tracking number..." style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 7, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
              </div>
            )}

            {/* Status action buttons */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button style={s.btnSm('#6c757d')} onClick={() => printBill(detail.id)}>🖨 Reprint</button>
              {detail.status === 'pending' && <button style={s.btnSm('#3b82f6')} onClick={() => updateStatus(detail.id, 'printed')}>✅ Mark Printed</button>}
              {detail.status === 'printed' && <button style={s.btnSm('#f59e0b')} onClick={() => updateStatus(detail.id, 'packed')}>📦 Mark Packed</button>}
              {detail.status === 'packed' && <button style={s.btnSm('#8b5cf6')} onClick={() => updateStatus(detail.id, 'shipped')}>🚚 Mark Shipped</button>}
              {detail.status === 'shipped' && <button style={s.btnSm('#22c55e')} onClick={() => updateStatus(detail.id, 'delivered')}>🎉 Mark Delivered</button>}
              {!['delivered','cancelled'].includes(detail.status) && <button style={s.btnSm('#ef4444')} onClick={() => updateStatus(detail.id, 'cancelled')}>✕ Cancel</button>}
            </div>

            {/* Timeline */}
            <div style={{ marginTop: 16, fontSize: 11, color: '#6c757d' }}>
              {detail.printed_at && <div>✅ Printed: {detail.printed_at}</div>}
              {detail.packed_at && <div>📦 Packed: {detail.packed_at}</div>}
              {detail.shipped_at && <div>🚚 Shipped: {detail.shipped_at}</div>}
              {detail.delivered_at && <div>🎉 Delivered: {detail.delivered_at}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
